import styled from '@emotion/styled';
import { ui, when, s } from '@owenscorning/pcb.alpha';
import _ from 'lodash';

// eslint-disable-next-line import/no-cycle
import AcousticResultsTable from './helpers/AcousticResultsTable';
import { openAcousticSelectorModal } from './helpers/AcousticSelectorModal';
import {
  getDocTitle,
  getDocSize,
  getDocDescription,
  getDocFilename,
  getDocFormat,
  getAssemblyResults,
  getFilterTargets,
  isCoveredByGuidePdf,
  resultCountLabel,
  splitAssemblyAttributes,
} from './helpers/acousticSelectorHelpers';
// eslint-disable-next-line import/no-cycle
import itemListResponse, { generateFilterOptions } from './helpers/item_list_response';
import pathToContent from './helpers/path_to_content';
import cmsApi from '../../../../cms_api';
import { expandRef, wrapRef } from '../../../../data';
import useReference from '../../../../hooks/use_reference';
import Theme from '../../../../themes/index';
import InfoIcon from '../../../ComponentLibrary/oc-info-icon';
import RichText from '../../../OC/PageBuilder/RichText';
import Text from '../../../OC/PageBuilder/utilities/Text';
import Icon from '../../../OC/oc-icon';
import { connectLocation } from '../../../location/LocationConnectors';
import { digChoicesFromData, digItemAttributesFromData } from '../../Library/AttributeSet';

// Styled Components
const ResultItem = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  position: relative;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  gap: 16px;
  position: relative;
  text-decoration: none;
  width: 100%;

  &:hover {
    > * {
      color: initial;
    }
  }
`;

const Heading = styled.strong`
  color: #000000;
  display: inline-block;
  margin-bottom: 8px;
  position: relative;
  width: 100%;
  text-transform: uppercase;
  font-size: 20px;
  font-weight: 700;
  line-height: 20px;

  &:hover {
    color: ${Theme.colors.brand};
  }
`;

const DocumentDetails = styled.a`
  align-items: center;
  display: flex;
  margin-bottom: 8px;
  position: relative;
  text-decoration: none;
  cursor: pointer;
  width: fit-content;

  &:focus {
    outline: none;
  }

  &:hover {
    text-decoration: underline;
    color: #333;
  }

  span {
    color: ${Theme.colors.greyShade1};
    margin-right: 8px;
  }
`;

const DetailIcon = styled(Icon)`
  &.detail-icon {
    color: ${props => props.color || '#000000'};
  }
`;

const Description = styled.div`
  & > div > div {
    margin: 0;
  }

  p {
    margin: 8px 0;
  }
`;

const AttributeList = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 8px 16px;

  ${props =>
    props.splitLayout &&
    `
    @media(min-width: 768px) {
      flex-direction: row;

      > span {
        @media(min-width: 768px) {
          width: calc(50% - 8px);
        }
      }
    }
  `}
`;

const Attribute = styled.span`
  align-item: center;
  color: ${Theme.colors.textGrey};
  display: flex;
  font-size: 14px;
  position: relative;
  width: 100%;

  strong {
    margin-right: 8px;
  }
`;

const EmailLink = styled.a`
  cursor: pointer;
  display: inline-block;
  flex-shrink: 0;
  font-size: 19px;
  padding: 24px;
`;

const ThumbnailImage = styled.img`
  width: 120px;
  height: auto;
  max-height: 120px;
  flex-shrink: 0;
  object-fit: contain;
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const TransparentButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  cursor: pointer;
`;

const InsulationMaterialNotice = styled.div`
  align-items: flex-start;
  color: ${Theme.colors.textGrey};
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 8px;
  line-height: 20px;
  margin-top: 24px;
  position: relative;
  width: 100%;
`;

const InsulationMaterialInfoIcon = styled(InfoIcon)`
  position: static;
  flex: 0 0 auto;
  margin-top: 2px;

  && > div:before {
    display: none;
  }
`;

const tooltipStyle = {
  bottom: 'calc(100% + 8px)',
  boxSizing: 'border-box',
  left: 4,
  right: 8,
  top: 'auto',
  transform: 'none',
  width: 'auto',
  zIndex: 1,
};

const insulationMaterialTooltip = [
  'QuietZone PINK Next Gen Fiberglas and Thermafiber SAFB are considered equivalent for the assemblies listed,',
  'except where a specific assembly is identified as having a different fire resistance rating.',
].join(' ');

const ResultCount = styled.span`
  color: ${Theme.colors.textGrey};
  display: block;
  font-size: 14px;
  margin-bottom: 8px;
`;

export const AcousticInsulationMaterialNotice = () => (
  <InsulationMaterialNotice>
    <span>QuietZone PINK Next Gen Fiberglas / Thermafiber SAFB</span>
    <InsulationMaterialInfoIcon
      clickHandler={() => {}}
      tooltip
      tooltipPosition="top"
      tooltipStyle={tooltipStyle}
      tooltipText={insulationMaterialTooltip}
    />
  </InsulationMaterialNotice>
);

AcousticInsulationMaterialNotice.filterValueType = 'singular';

export const addOriginToUrl = url => {
  if (!url) return '';

  const { origin } = window.location;
  if (url.startsWith('http')) return url;

  return `${origin}${url}`;
};

const openDetailModal = ({ result, attributes }) => {
  openAcousticSelectorModal({ result, attributes });
};

export const AcousticSelectorResultBlock = connectLocation(({ t, attributes, ...result }) => {
  const title = getDocTitle(result);
  const docUrl = pathToContent(result);
  const docFormat = getDocFormat(result);
  const fileTypes = {
    doc: { icon: 'file-word-o', label: 'DOC', color: '#00688C' },
    docx: { icon: 'file-word-o', label: 'DOCX', color: '#00688C' },
    dwg: { icon: 'file-o', label: 'DWG', color: '#000000' },
    external: { icon: 'external-link', label: 'External Link', color: '#000000' },
    pdf: { icon: 'file-pdf-o', label: 'PDF', color: '#CE1126' },
    rvt: { icon: 'file-o', label: 'RVT', color: '#000000' },
    xls: { icon: 'file-excel-o', label: 'XLS', color: '#336C40' },
    xlsx: { icon: 'file-excel-o', label: 'XLSX', color: '#336C40' },
  };
  const docFormatIcon = fileTypes[docFormat]?.icon || 'file-o';
  const docFormatLabel = fileTypes[docFormat]?.label || docFormat?.toUpperCase() || '';
  const docFormatColor = fileTypes[docFormat]?.color;
  const size = getDocSize(result);
  const shortDescription = getDocDescription(result);
  // eslint-disable-next-line no-undef
  const mailToSubject = t(`builder.documents.mailto.subject`, { site_name: PB_SITE_NAME });
  // eslint-disable-next-line no-undef
  const mailToBody = t(`builder.documents.mailto.body`, { site_name: PB_SITE_NAME });

  const assemblyResults = getAssemblyResults(result);
  const { rowAttributes, cardAttributes } = splitAssemblyAttributes(attributes, assemblyResults);

  const infoItems = cardAttributes
    .map(attribute => {
      const { label, field, type, of } = attribute;

      if (!label) {
        return null;
      }

      const resultValue = _.get(result, field);
      switch (type) {
        case 'choices':
          return {
            label,
            value: _.sortBy(Array.wrap(resultValue), a => {
              const keys = Object.keys(of || {});
              return keys.indexOf(a);
            }).map(v => of?.[v]?.label || v),
          };
        default:
          return { label, value: resultValue?.toString() };
      }
    })
    .filter(x => x.value?.length > 0);

  const infoItemComponents = infoItems.map(infoItem => (
    <Attribute key={infoItem.label}>
      <strong>{infoItem.label}</strong> {Array.wrap(infoItem.value).join(', ')}
    </Attribute>
  ));

  const thumbnailUrl = result?.contents?.thumbnail;

  return (
    <ResultItem>
      <Container>
        {thumbnailUrl && (
          <TransparentButton
            onClick={() => openDetailModal({ result, attributes })}
            data-track="open-modal"
            data-track-modal-name="Acoustic Selector Details"
            data-track-assembly-number={title}
          >
            <ThumbnailImage src={thumbnailUrl} alt={title} />
          </TransparentButton>
        )}

        <ContentWrapper>
          <TransparentButton
            onClick={() => openDetailModal({ result, attributes })}
            data-track="open-modal"
            data-track-modal-name="Acoustic Selector Details"
            data-track-assembly-number={title}
          >
            <Heading className="document-heading">
              <Text content={title} />
            </Heading>
          </TransparentButton>

          {isCoveredByGuidePdf(result) && (
            <DocumentDetails
              target="_blank"
              data-track="file-download"
              data-track-doc-type="Acoustic Selector"
              data-track-element-location="search results"
              data-track-file-name={getDocFilename(result)}
              href={docUrl}
            >
              {(docFormat || size) && (
                <>
                  {docFormat && <DetailIcon className="detail-icon" color={docFormatColor} type={docFormatIcon} />}
                  <span>{`${docFormatLabel} ${size}`.trim()}</span>
                </>
              )}
            </DocumentDetails>
          )}

          {assemblyResults.length > 0 && <ResultCount>{resultCountLabel(assemblyResults.length)}</ResultCount>}

          {result.showDescription && shortDescription && (
            <Description>
              <RichText content={shortDescription} />
            </Description>
          )}

          {rowAttributes.length > 0 && <AcousticResultsTable results={assemblyResults} rowAttributes={rowAttributes} />}

          <AttributeList splitLayout={infoItemComponents.length > 3}>{infoItemComponents}</AttributeList>
        </ContentWrapper>
      </Container>

      {result.showEmailDocument && (
        <EmailLink
          aria-label="Email document"
          href={`mailto:?subject=${mailToSubject}&body=${mailToBody}%0A%0A${docUrl}`}
        >
          <Icon type="envelope-o" />
        </EmailLink>
      )}
    </ResultItem>
  );
});

const searchFields = [
  ['metadata', 'settings', 'attributes', 'keywords'],
  ['metadata', 'settings', 'attributes', 'acoustic_selector_attributes', 'assembly_number'],
  ['metadata', 'settings', 'attributes', 'acoustic_selector_attributes', 'drawing_name'],
];

const acousticSelectorItemListResponse = (
  items,
  attributes,
  showFilters,
  enabledFilters,
  showEmailDocument,
  showDescription,
  allFilters,
  filtersCollapsed
) => {
  let results = items;
  if (items && !Array.isArray(items)) {
    results = items.results;
  }

  const filters = generateFilterOptions(showFilters, enabledFilters, allFilters, null, {
    startCollapsed: filtersCollapsed,
  });

  return itemListResponse({
    items: results,
    filters: filters
      ? {
          ...filters,
          insulationMaterialNotice: {
            component: AcousticInsulationMaterialNotice,
            options: [],
            suppressCount: true,
            valueType: 'none',
          },
        }
      : filters,
    Component: props => (
      <AcousticSelectorResultBlock
        attributes={attributes}
        showEmailDocument={showEmailDocument}
        showDescription={showDescription}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    ),
    searchFields,
    enableSearch: true,
    getFilterTargets,
  });
};

export default {
  ItemList: {
    availableIn: ['www.owenscorning.com'],
    name: 'Acoustic Selector',
    meta: async () => {
      const query = {
        filter: {
          type: 'Cms::Content::AttributeSet',
          name: 'Acoustic Selector',
          route: '/insulation',
        },
        fields: {
          '*': 'contents,metadata',
        },
      };
      const data = await cmsApi.get_single_for_language(
        query,
        // eslint-disable-next-line no-undef
        Board.build.language
      );
      const hasFilters = !!data;
      const filterChoices = hasFilters
        ? await digChoicesFromData('metadata.settings.attributes.acoustic_selector_attributes', data)
        : null;
      const taxonomies = hasFilters
        ? Object.fromEntries(Object.entries(filterChoices).map(([key, entry]) => [key, entry.label]))
        : {};
      const filters = hasFilters
        ? ui`Form`.of(
            Object.fromEntries(
              Object.entries(filterChoices).map(([key, { of, ...entry }]) => [
                key,
                ui`Choices`.of(of)({ ...entry, multiple: true, includeParentTree: false }),
              ])
            )
          )
        : null;
      const defaultEnabledFilterKeys = Object.keys(taxonomies);
      const enabledFilters = hasFilters
        ? ui`ChoicesDraggable`.of(taxonomies)({
            // ** here is where we need to add the filter UI type
            label: 'Filters',
            visible: when`../showFilters`.is.equal.to(true).then(true).otherwise(false),
            default: {
              enabledFilters: defaultEnabledFilterKeys,
              orderedList: Object.keys(taxonomies).map(k => ({ [k]: defaultEnabledFilterKeys.indexOf(k) >= 0 })),
            },
          })
        : null;
      const itemAttributeOptions = digItemAttributesFromData(data);
      const itemAttributes =
        itemAttributeOptions.length > 0
          ? ui`Choices`.of(itemAttributeOptions)({
              label: 'Item Attributes',
              sublabel: 'Select Item Attributes to Display:',
              mode: ui`Choices/Mode/Vertical`,
              multiple: true,
              tip: 'If more than three items are selected, they will shift to two columns.',
            })
          : null;
      return {
        source: ui`Choices`.of({
          all: 'All Items',
          filtered: 'Filtered',
          specific: 'Select Specific Items',
        })({
          label: 'Select Structure',
          default: 'all',
          mode: ui`Choices/Mode/Dropdown`,
        }),
        filters: ui`List/Item`.of(filters)({
          standalone: true,
          title: 'Acoustic Selector',
          label: 'Filters',
          visible: when`../source`.is.equal.to('filtered'),
        }),
        items: ui`List`.of({
          item: ui`Search`({
            startOpen: when`../item`.isnt.present.then(true).otherwise(false),
            label: 'Document',
            dataset: 'acoustic_selector',
            set: value => {
              // TODO: handle empty/null value?
              const ref = wrapRef('Cms::Content', { type: 'Document::AcousticSelector', id: value.id });
              expandRef(ref);
              return ref;
            },
            get: value => {
              const { results, error, loading } = useReference(value);
              if (loading) {
                return <i>Loading...</i>;
              }
              if (error) {
                return <span>{error}</span>;
              }
              return results;
            },
          }),
        })({
          singular: 'Document',
          title: 'proper_name',
          label: 'Documents',
          visible: when`../source`.is.equal.to('specific').then(true).otherwise(false),
        }),
        showFilters: ui`Switch`({
          label: 'Filter Pane',
          default: hasFilters,
          disabled: !hasFilters,
        }),
        filtersCollapsed: ui`Switch`({
          label: 'Filters Collapsed',
          default: true,
          visible: when`../showFilters`.is.equal.to(true).then(true).otherwise(false),
        }),
        [s._]: ui`Tip`.of(`Cannot find AttributeSet <b>Acoustic Selector Attributes</b> in a matching language`)({
          visible: !hasFilters,
        }),
        [s._]: ui`Tip`.of(
          // eslint-disable-next-line max-len
          'If the item list you wish to display is to be very long, we recommend turning on the Filter Side Pane to allow users to filter down the results to their needs.'
        )({
          visible: hasFilters,
        }),
        enabledFilters,
        showDescription: ui`Switch`({
          label: 'Show Item Description',
          default: false,
        }),
        showEmailDocument: ui`Switch`({
          label: 'Show Email Document Button',
          default: false,
        }),
        itemAttributes,
      };
    },
    view: (data = null) => {
      const {
        data: items,
        meta: { parameters, filters, attributes = [] },
      } = data || { meta: {} };
      const { showFilters, enabledFilters, showEmailDocument, showDescription, filtersCollapsed } = parameters || {};

      return acousticSelectorItemListResponse(
        items,
        attributes,
        showFilters,
        enabledFilters,
        showEmailDocument,
        showDescription,
        filters,
        filtersCollapsed
      );
    },
  },
};
